import MappingBase from './mappingBase.js';
import BookingService from './bookingService.js';
import PaymentInfo from './paymentInfo.js';

/**
 * Booking Model
 * Represents a complete booking with services and payment information
 */
class Booking extends MappingBase {
  constructor({
    bookingId = null,
    salonId = null,
    customerId = null,
    staffMemberId = null,
    bookingDate = null,
    totalDuration = 0,
    totalPrice = 0,
    status = 0,          // -1=cancelled, 0=pending, 1=confirmed, 2=completed
    services = [],
    payment = null,
    notes = null,
    createdAt = null,
    updatedAt = null
  } = {}) {
    super();
    this.bookingId = bookingId;
    this.salonId = salonId;
    this.customerId = customerId;
    this.staffMemberId = staffMemberId;
    this.bookingDate = bookingDate ? new Date(bookingDate) : null;
    this.totalDuration = totalDuration;
    this.totalPrice = totalPrice;
    this.status = status;
    this.services = services.map(s => 
      s instanceof BookingService ? s : new BookingService(s)
    );
    this.payment = payment instanceof PaymentInfo 
      ? payment 
      : new PaymentInfo(payment || {});
    this.notes = notes;
    this.createdAt = createdAt ? new Date(createdAt) : null;
    this.updatedAt = updatedAt ? new Date(updatedAt) : null;
  }

  /**
   * Alias mapping for field name conversion
   * Maps backend aliases to full field names
   */
  static _aliasMapping = {
    bid: "bookingId",
    sid: "salonId",
    cid: "customerId",
    smid: "staffMemberId",
    bdt: "bookingDate",
    tdu: "totalDuration",
    tpr: "totalPrice",
    st: "status",
    svcs: "services",
    pmt: "payment",
    nt: "notes",
    cat: "createdAt",
    uat: "updatedAt"
  };

  /**
   * Booking Status Constants
   */
  static STATUS = {
    CANCELLED: -1,
    PENDING: 0,
    CONFIRMED: 1,
    COMPLETED: 2
  };

  // ===== Status Helper Methods =====

  /**
   * Check if booking is pending
   */
  get isPending() {
    return this.status === Booking.STATUS.PENDING;
  }

  /**
   * Check if booking is confirmed
   */
  get isConfirmed() {
    return this.status === Booking.STATUS.CONFIRMED;
  }

  /**
   * Check if booking is completed
   */
  get isCompleted() {
    return this.status === Booking.STATUS.COMPLETED;
  }

  /**
   * Check if booking is cancelled
   */
  get isCancelled() {
    return this.status === Booking.STATUS.CANCELLED;
  }

  /**
   * Get booking status label
   */
  get statusLabel() {
    switch (this.status) {
      case Booking.STATUS.CANCELLED:
        return 'Cancelled';
      case Booking.STATUS.PENDING:
        return 'Pending';
      case Booking.STATUS.CONFIRMED:
        return 'Confirmed';
      case Booking.STATUS.COMPLETED:
        return 'Completed';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get status color for UI
   */
  get statusColor() {
    switch (this.status) {
      case Booking.STATUS.CANCELLED:
        return 'error';
      case Booking.STATUS.PENDING:
        return 'warning';
      case Booking.STATUS.CONFIRMED:
        return 'info';
      case Booking.STATUS.COMPLETED:
        return 'success';
      default:
        return 'grey';
    }
  }

  // ===== Payment Helper Methods =====

  /**
   * Check if booking is fully paid
   */
  get isFullyPaid() {
    return this.payment.isPaid;
  }

  /**
   * Check if booking has partial payment
   */
  get hasPartialPayment() {
    return this.payment.isPartial;
  }

  /**
   * Check if booking payment is pending
   */
  get isPendingPayment() {
    return this.payment.isPending;
  }

  // ===== Formatting Methods =====

  /**
   * Get formatted total duration (e.g., "2h 30m")
   */
  get formattedDuration() {
    if (!this.totalDuration) return "0m";
    
    const hours = Math.floor(this.totalDuration / 60);
    const minutes = this.totalDuration % 60;
    
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  /**
   * Get formatted total price
   */
  get formattedPrice() {
    return `$${this.totalPrice.toFixed(2)}`;
  }

  /**
   * Get formatted booking date
   */
  get formattedDate() {
    if (!this.bookingDate) return '';
    
    return this.bookingDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Get formatted booking time
   */
  get formattedTime() {
    if (!this.bookingDate) return '';
    
    return this.bookingDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get formatted booking date and time
   */
  get formattedDateTime() {
    if (!this.bookingDate) return '';
    
    return `${this.formattedDate} at ${this.formattedTime}`;
  }

  // ===== Computed Properties =====

  /**
   * Check if booking is in the past
   */
  get isPast() {
    if (!this.bookingDate) return false;
    return this.bookingDate < new Date();
  }

  /**
   * Check if booking is upcoming
   */
  get isUpcoming() {
    if (!this.bookingDate) return false;
    return this.bookingDate >= new Date() && !this.isCancelled;
  }

  /**
   * Get number of services in booking
   */
  get serviceCount() {
    return this.services.length;
  }
}

export default Booking;
